import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Card, CardContent, Stack, Chip, IconButton, Button,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, Snackbar, Alert,
  CircularProgress, Tooltip, FormControlLabel, Switch, Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import BlockIcon from "@mui/icons-material/Block";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import EventIcon from "@mui/icons-material/Event";
import {
  fetchEvents, createEvent, updateEvent, closeEvent, deleteEvent,
  uploadBanner, fetchQrBlobUrl, downloadQr, bannerUrl, publicEventUrl,
  SpecialEvent, EventStatus, STATUS_LABEL, STATUS_COLOR, formatEventDate,
} from "./specialEventsApi";

/* DCC brand tokens — match the dashboard/login design language so the
   admin module reads as part of the same product, not bolted-on. */
const NAVY = "#0d1b2a";
const GOLD = "#c9a84c";

interface EventFormState {
  title: string;
  /** Custom URL slug. Empty on create = server auto-generates from title. */
  slug: string;
  description: string;
  event_date: string;        // yyyy-mm-dd
  event_time: string;        // free text e.g. "5:30 PM"
  location: string;
  admission_text: string;
  banner_image: string;
  status: EventStatus;
  max_capacity: string;      // string for the input; coerced on submit
  registration_enabled: boolean;
}

const emptyForm: EventFormState = {
  title: "", slug: "", description: "", event_date: "", event_time: "",
  location: "", admission_text: "Free (RSVP Required)",
  banner_image: "", status: "active", max_capacity: "",
  registration_enabled: true,
};

/** Normalize a slug as the user types: lowercase, collapse whitespace to
 *  hyphens, strip everything else, trim leading/trailing hyphens. Matches
 *  the backend's `^[a-z0-9]+(?:-[a-z0-9]+)*$` validator so the form can't
 *  send a value the server will reject. */
const normalizeSlug = (raw: string): string =>
  raw
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");

const EventsList: React.FC = () => {
  const [events, setEvents]   = useState<SpecialEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [snack, setSnack] = useState<{ open: boolean; msg: string; severity: "success" | "error" | "info" }>({
    open: false, msg: "", severity: "success",
  });
  const showSnack = (msg: string, severity: "success" | "error" | "info" = "success") =>
    setSnack({ open: true, msg, severity });

  // Form dialog state (used for both Create and Edit — distinguished by editingId)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<EventFormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // QR dialog state — opens after Create or via the QR icon on the row.
  // qrBlobUrl holds an authenticated-fetched blob URL for the PNG so
  // <img src> works (the endpoint is sanctum-protected and <img> can't
  // attach a bearer token, so a plain `${API_URL}/.../qr-code` src gets
  // a 401 and renders blank).
  const [qrEvent,    setQrEvent]    = useState<SpecialEvent | null>(null);
  const [qrBlobUrl,  setQrBlobUrl]  = useState<string | null>(null);
  const [qrLoading,  setQrLoading]  = useState(false);

  useEffect(() => {
    if (!qrEvent) {
      setQrBlobUrl(null);
      return;
    }
    let cancelled = false;
    let urlForCleanup: string | null = null;
    setQrLoading(true);
    fetchQrBlobUrl(qrEvent.id, qrEvent.slug, 400)
      .then((url) => {
        if (cancelled) { URL.revokeObjectURL(url); return; }
        urlForCleanup = url;
        setQrBlobUrl(url);
      })
      .catch(() => { if (!cancelled) showSnack("Failed to load QR code", "error"); })
      .finally(() => { if (!cancelled) setQrLoading(false); });
    return () => {
      cancelled = true;
      if (urlForCleanup) URL.revokeObjectURL(urlForCleanup);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrEvent?.id]);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try { setEvents(await fetchEvents()); }
    catch { showSnack("Failed to load events", "error"); }
    setLoading(false);
  }, []);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (e: SpecialEvent) => {
    setEditingId(e.id);
    setForm({
      title: e.title,
      slug: e.slug,
      description: e.description ?? "",
      event_date: e.event_date ?? "",
      event_time: e.event_time ?? "",
      location: e.location ?? "",
      admission_text: e.admission_text ?? "",
      banner_image: e.banner_image ?? "",
      status: e.status,
      max_capacity: e.max_capacity?.toString() ?? "",
      registration_enabled: e.registration_enabled,
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const path = await uploadBanner(file);
      setForm((f) => ({ ...f, banner_image: path }));
      showSnack("Banner uploaded");
    } catch (e: any) {
      showSnack(e.message ?? "Upload failed", "error");
    }
    setUploading(false);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.event_date) {
      showSnack("Title and Date are required", "error");
      return;
    }
    setSaving(true);
    const payload: any = {
      title: form.title.trim(),
      description: form.description || null,
      event_date: form.event_date,
      event_time: form.event_time || null,
      location: form.location || null,
      admission_text: form.admission_text || null,
      banner_image: form.banner_image || null,
      status: form.status,
      max_capacity: form.max_capacity ? Number(form.max_capacity) : null,
      registration_enabled: form.registration_enabled,
    };
    // Slug is sent only when non-empty:
    //   - on create, an empty slug tells the server to auto-generate
    //   - on update, an empty slug would fail the `sometimes` check —
    //     we just omit it so the existing slug is preserved
    const trimmedSlug = form.slug.trim();
    if (trimmedSlug !== "") payload.slug = trimmedSlug;
    try {
      const saved = editingId
        ? await updateEvent(editingId, payload)
        : await createEvent(payload);
      showSnack(editingId ? "Event updated" : "Event created");
      setDialogOpen(false);
      await loadEvents();
      // After a CREATE, auto-open the QR / copy-link dialog so the admin
      // gets the shareable URL without an extra click. Skipped on edit.
      if (!editingId) setQrEvent(saved);
    } catch (e: any) {
      showSnack(e.message ?? "Save failed", "error");
    }
    setSaving(false);
  };

  const handleClose = async (id: number) => {
    if (!window.confirm("Close this event? Registrations will be blocked.")) return;
    try { await closeEvent(id); showSnack("Event closed"); loadEvents(); }
    catch (e: any) { showSnack(e.message ?? "Failed", "error"); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Permanently delete this event AND all its registrations? This cannot be undone.")) return;
    try { await deleteEvent(id); showSnack("Event deleted"); loadEvents(); }
    catch (e: any) { showSnack(e.message ?? "Failed", "error"); }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showSnack("Link copied to clipboard");
    } catch {
      showSnack("Could not copy — please copy manually", "error");
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#f5f6fa", minHeight: "100vh" }}>
      {/* Header */}
      <Card elevation={0} sx={{
        mb: 2.5, borderRadius: 3,
        background: `linear-gradient(135deg, ${NAVY} 0%, #1e3a5f 100%)`, color: "#fff",
      }}>
        <CardContent sx={{ py: 2.5, px: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Stack direction="row" alignItems="center" gap={2}>
              <EventIcon sx={{ fontSize: 38, opacity: 0.85 }} />
              <Box>
                <Typography variant="h5" fontWeight={800} lineHeight={1}>Special Events</Typography>
                <Typography variant="caption" sx={{ opacity: 0.75, mt: 0.5, display: "block" }}>
                  Community events with public QR registration · {events.length} total
                </Typography>
              </Box>
            </Stack>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreate}
              sx={{ bgcolor: GOLD, color: NAVY, fontWeight: 700, borderRadius: 2, "&:hover": { bgcolor: "#b89540" } }}
            >
              New Event
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Events table */}
      <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e8e8e8" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#fafbfc" }}>
                <TableCell sx={{ fontWeight: 700, color: NAVY }}>Event</TableCell>
                <TableCell sx={{ fontWeight: 700, color: NAVY }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700, color: NAVY }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, color: NAVY, textAlign: "center" }}>Registrations</TableCell>
                <TableCell sx={{ fontWeight: 700, color: NAVY, textAlign: "right" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell></TableRow>
              )}
              {!loading && events.length === 0 && (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No events yet. Click "New Event" to create your first one.
                </TableCell></TableRow>
              )}
              {events.map((e) => {
                const regCount = e.active_registrations_count ?? e.registration_count ?? 0;
                const cap = e.max_capacity;
                return (
                  <TableRow key={e.id} hover>
                    <TableCell>
                      <Stack direction="row" alignItems="center" gap={1}>
                        {e.banner_image && (
                          <Box
                            component="img"
                            src={bannerUrl(e.banner_image) ?? ""}
                            sx={{ width: 40, height: 40, objectFit: "cover", borderRadius: 1, flexShrink: 0 }}
                            alt=""
                            // Hide the broken-image icon if storage:link
                            // hasn't been run yet or the file was moved.
                            // The row still renders cleanly without it.
                            onError={(ev) => { (ev.currentTarget as HTMLImageElement).style.display = "none"; }}
                          />
                        )}
                        <Box>
                          <Typography variant="body2" fontWeight={700} color={NAVY}>{e.title}</Typography>
                          <Typography variant="caption" color="text.secondary">/reg/{e.slug}</Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatEventDate(e.event_date)}</Typography>
                      {e.event_time && (
                        <Typography variant="caption" color="text.secondary">{e.event_time}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" gap={0.5} flexWrap="wrap">
                        <Chip
                          label={STATUS_LABEL[e.status]}
                          size="small"
                          sx={{
                            height: 20, fontSize: 10, fontWeight: 700,
                            bgcolor: `${STATUS_COLOR[e.status]}18`,
                            color: STATUS_COLOR[e.status],
                            border: `1px solid ${STATUS_COLOR[e.status]}30`,
                          }}
                        />
                        {!e.registration_enabled && (
                          <Chip label="Paused" size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: "#fef3c7", color: "#92400e" }} />
                        )}
                        {e.is_full && (
                          <Chip label="Full" size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: "#fee2e2", color: "#991b1b" }} />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={700} color={NAVY}>
                        {regCount}{cap ? ` / ${cap}` : ""}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" gap={0.3} justifyContent="flex-end">
                        <Tooltip title="Open public page">
                          <IconButton size="small" component="a" href={publicEventUrl(e.slug)} target="_blank" rel="noreferrer">
                            <OpenInNewIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Copy link">
                          <IconButton size="small" onClick={() => copyToClipboard(publicEventUrl(e.slug))}>
                            <ContentCopyIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Show QR code">
                          <IconButton size="small" onClick={() => setQrEvent(e)}>
                            <QrCode2Icon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(e)}>
                            <EditIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        {e.status !== "closed" && (
                          <Tooltip title="Close event">
                            <IconButton size="small" onClick={() => handleClose(e.id)} sx={{ color: "#f59e0b" }}>
                              <BlockIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDelete(e.id)} sx={{ color: "#ef4444" }}>
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: NAVY, borderBottom: "1px solid #e8e8e8" }}>
          {editingId ? "Edit Event" : "New Event"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack gap={2} pt={1}>
            <TextField
              label="Event Title *"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              fullWidth
              placeholder="Tamil Community Meet and Greet with Mayor Olivia Chow"
            />
            <TextField
              label="URL Slug"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: normalizeSlug(e.target.value) })}
              fullWidth
              placeholder={editingId ? "" : "leave blank to auto-generate from title"}
              InputProps={{
                startAdornment: (
                  <Typography
                    sx={{
                      color: "text.secondary", fontSize: "0.9rem", whiteSpace: "nowrap",
                      mr: 0.5, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    }}
                  >
                    {window.location.origin}/reg/
                  </Typography>
                ),
              }}
              helperText={
                editingId
                  ? "Editing the slug will break any QR codes already printed. Re-print after saving."
                  : "Lowercase letters, digits and hyphens only. Leave blank to auto-generate."
              }
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              fullWidth multiline rows={3}
            />
            <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
              <TextField
                label="Date *"
                type="date"
                value={form.event_date}
                onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Time"
                value={form.event_time}
                onChange={(e) => setForm({ ...form, event_time: e.target.value })}
                placeholder="5:30 PM"
                fullWidth
              />
            </Stack>
            <TextField
              label="Location"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              fullWidth multiline rows={2}
              placeholder="Durham Convention Centre, 630 Beck Crescent, Ajax, ON L1Z 1C9"
            />
            <TextField
              label="Admission Text"
              value={form.admission_text}
              onChange={(e) => setForm({ ...form, admission_text: e.target.value })}
              fullWidth
              placeholder="Free (RSVP Required)"
            />

            {/* Banner upload */}
            <Box sx={{ p: 2, border: "1px dashed #cbd5e1", borderRadius: 2 }}>
              <Typography variant="body2" fontWeight={700} color={NAVY} sx={{ mb: 1 }}>Banner Image</Typography>
              {form.banner_image ? (
                <Stack direction="row" alignItems="center" gap={2}>
                  <Box
                    component="img"
                    src={bannerUrl(form.banner_image) ?? ""}
                    sx={{ width: 120, height: 70, objectFit: "cover", borderRadius: 1, border: "1px solid #e8e8e8" }}
                    alt=""
                  />
                  <Button size="small" color="error" onClick={() => setForm({ ...form, banner_image: "" })}>
                    Remove
                  </Button>
                </Stack>
              ) : (
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={uploading ? <CircularProgress size={14} /> : <UploadFileIcon />}
                  disabled={uploading}
                  sx={{ textTransform: "none" }}
                >
                  {uploading ? "Uploading..." : "Choose image"}
                  <input
                    hidden
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleUpload(f);
                      e.target.value = "";
                    }}
                  />
                </Button>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                PNG / JPG / WEBP up to 4 MB. Shown above the form on the public page.
              </Typography>
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={form.status}
                  label="Status"
                  onChange={(e) => setForm({ ...form, status: e.target.value as EventStatus })}
                >
                  <MenuItem value="draft">Draft (hidden from public)</MenuItem>
                  <MenuItem value="active">Active (accepting RSVPs)</MenuItem>
                  <MenuItem value="closed">Closed (RSVPs blocked)</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Capacity"
                type="number"
                value={form.max_capacity}
                onChange={(e) => setForm({ ...form, max_capacity: e.target.value })}
                helperText="Leave blank for unlimited"
                fullWidth
              />
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={form.registration_enabled}
                  onChange={(e) => setForm({ ...form, registration_enabled: e.target.checked })}
                />
              }
              label="Registration enabled"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={closeDialog} color="inherit">Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ bgcolor: NAVY, "&:hover": { bgcolor: "#1e3a5f" }, fontWeight: 700 }}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : (editingId ? "Update" : "Create")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR / Link share dialog */}
      <Dialog open={!!qrEvent} onClose={() => setQrEvent(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>
          Share &amp; QR Code
        </DialogTitle>
        <DialogContent dividers>
          {qrEvent && (
            <Stack gap={2} alignItems="center">
              <Typography variant="body2" fontWeight={700} color={NAVY} textAlign="center">
                {qrEvent.title}
              </Typography>
              <Box
                sx={{
                  width: 280, height: 280, border: "1px solid #e8e8e8",
                  borderRadius: 2, bgcolor: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                {qrLoading || !qrBlobUrl
                  ? <CircularProgress size={28} sx={{ color: NAVY }} />
                  : <Box component="img" src={qrBlobUrl} alt="QR code" sx={{ width: 260, height: 260 }} />}
              </Box>
              <Divider sx={{ width: "100%" }} />
              <TextField
                value={publicEventUrl(qrEvent.slug)}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
              />
              <Stack direction="row" gap={1} sx={{ width: "100%" }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  onClick={() => copyToClipboard(publicEventUrl(qrEvent.slug))}
                >
                  Copy Link
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() =>
                    downloadQr(qrEvent.id, qrEvent.slug, 800)
                      .catch(() => showSnack("Download failed", "error"))
                  }
                  startIcon={<QrCode2Icon />}
                  sx={{ bgcolor: NAVY, "&:hover": { bgcolor: "#1e3a5f" } }}
                >
                  Download QR
                </Button>
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrEvent(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snack.severity} variant="filled" sx={{ borderRadius: 2 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EventsList;
